import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronDown, MapPin, Navigation, Search, User } from "lucide-react";
import { LanguageToggle } from "@/components/i18n/LanguageToggle";
import { listCityVenueCounts } from "@/data/restaurants";
import { useGeolocation } from "@/hooks/useGeolocation";
import { useTranslation } from "@/i18n/I18nProvider";
import { useAuthStore } from "@/services/authStore";
import { useShowcase } from "@/services/showcase";
import { CITIES, citySlug, type City } from "@/lib/cities";
import { foldText } from "@/lib/feedFilter";
import { haversineKm } from "@/lib/geo";
import { cn } from "@/lib/cn";
import { siteUrl, usePageMeta } from "@/lib/pageMeta";

// Same lazy account chunk the feed uses — the auth UI only downloads if the
// visitor actually taps the account button on the gate.
const AccountSidebar = lazy(() => import("@/components/account/AccountSidebar"));

/**
 * The app's front door (`/`). A minimal, phone-first city gate: pick a city,
 * then continue into the live feed at `/app?city=<id>`. The marketing page
 * now lives at `/landing`.
 *
 * A city is only selectable once it has at least one venue — per-city counts
 * are fetched from the database, so cities light up automatically as the
 * operator adds restaurants there. Cities with venues list first (A→Z, venue
 * count next to the name); empty cities trail last, greyed with a "soon" badge.
 */
export default function Welcome() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Inside the `/showcase-cluj` demo the gate offers only the demo city, with
  // its bundled count — no fetch, no location, no account entry point.
  const showcase = useShowcase();
  usePageMeta(
    showcase ? null : { title: t("META_WELCOME_TITLE"), description: t("META_WELCOME_DESC"), canonical: siteUrl("/") },
  );
  const cities = useMemo(
    () => (showcase ? CITIES.filter((c) => c.id === showcase.cityId) : CITIES),
    [showcase],
  );
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<City | null>(null);
  const [counts, setCounts] = useState<Record<string, number> | null>(null);
  const [countsError, setCountsError] = useState(false);
  // Bumped by the retry link — re-runs the counts fetch effect.
  const [countsTick, setCountsTick] = useState(0);
  const menuRef = useRef<HTMLDivElement>(null);
  // Silent on mount: an already-granted permission is reused right away, but
  // the page itself never opens a permission dialog. The prompt is instead
  // tied to the "Select a city" tap (see the picker button), so first-time
  // visitors get the nearest-city pin the moment they engage with the picker.
  const geo = useGeolocation({ mode: showcase ? "off" : "silent" });

  // Account & settings sidebar — same lazy mounted/open split as the feed so
  // the auth chunk loads once, on demand. Gives the gate the same top-right
  // account entry point the city feed has.
  const [accountOpen, setAccountOpen] = useState(false);
  const [accountMounted, setAccountMounted] = useState(false);
  const openAccount = () => {
    setAccountMounted(true);
    setAccountOpen(true);
  };
  const accountInitial = useAuthStore(
    (s) => s.consumerProfile?.display_name?.trim().charAt(0).toUpperCase() ?? null,
  );

  // The city whose centre is closest to the visitor — pinned to the top of the
  // picker (with its real available/soon state; a "soon" nearest city is honest
  // signal). Null until/unless location is silently available.
  const nearest = useMemo(() => {
    if (!geo.coords) return null;
    const here = geo.coords;
    return cities.reduce((best, c) =>
      haversineKm(here, c) < haversineKm(here, best) ? c : best,
    );
  }, [geo.coords, cities]);

  // Prefill the picker with the nearest city as soon as it's known — Continue
  // is then immediately actionable without opening the dropdown. Guards: only
  // once (a ref, so a later geo tick can't re-fire), never over a city the
  // visitor already picked, and only when the nearest city actually has venues
  // (a "soon" city would send Continue into an empty catalog). Fully changeable
  // — opening the picker and choosing another city overrides this.
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current || selected || !nearest) return;
    if ((counts?.[nearest.id] ?? 0) > 0) {
      prefilled.current = true;
      setSelected(nearest);
    }
  }, [nearest, counts, selected]);

  // Picker order: nearest first (when known — even a "soon" nearest is honest
  // signal), then cities with venues A→Z, then "soon" cities A→Z.
  const orderedCities = useMemo(() => {
    const sorted = [...cities].sort((a, b) => a.name.localeCompare(b.name, "ro"));
    const has = (c: City) => (counts?.[c.id] ?? 0) > 0;
    const grouped = [...sorted.filter(has), ...sorted.filter((c) => !has(c))];
    return nearest ? [nearest, ...grouped.filter((c) => c.id !== nearest.id)] : grouped;
  }, [nearest, counts, cities]);

  // Diacritic-insensitive city search. With no query the picker lists only
  // cities that have venues (one "More cities soon" line stands in for the
  // rest, instead of a wall of "Soon" badges); a search still finds a "soon"
  // city, so "Iași" answers "soon" rather than "no such city".
  const folded = foldText(query.trim());
  const filtered = folded
    ? orderedCities.filter((c) => foldText(c.name).includes(folded))
    : counts === null
      ? orderedCities
      : orderedCities.filter((c) => (counts[c.id] ?? 0) > 0);
  const hiddenSoon = !folded && counts !== null && filtered.length < orderedCities.length;

  // Roving keyboard highlight (APG listbox pattern): arrows move it, Enter
  // picks it, aria-activedescendant tells screen readers where it sits. -1 =
  // nothing highlighted (Enter then falls back to the first available match).
  const [activeIdx, setActiveIdx] = useState(-1);
  useEffect(() => {
    setActiveIdx(-1);
  }, [query, open]);
  useEffect(() => {
    if (activeIdx < 0) return;
    const city = filtered[activeIdx];
    if (city) document.getElementById(`city-opt-${city.id}`)?.scrollIntoView({ block: "nearest" });
    // filtered is derived state; activeIdx resets whenever it can go stale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIdx]);

  function onListKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (filtered.length === 0) return;
      const delta = e.key === "ArrowDown" ? 1 : -1;
      setActiveIdx((i) => {
        if (i === -1) return delta === 1 ? 0 : filtered.length - 1;
        return (i + delta + filtered.length) % filtered.length;
      });
      return;
    }
    if (e.key === "Home" || e.key === "End") {
      if (filtered.length === 0) return;
      e.preventDefault();
      setActiveIdx(e.key === "Home" ? 0 : filtered.length - 1);
      return;
    }
    if (e.key === "Enter") {
      const highlighted = activeIdx >= 0 ? filtered[activeIdx] : undefined;
      const target = highlighted ?? filtered.find((c) => isAvailable(c));
      if (target) choose(target);
    }
  }

  // How many venues each city currently has. A failed lookup falls back to
  // all-greyed (no crash) + a slim retry row, so an outage doesn't read as
  // "every city is coming soon" with no way out.
  useEffect(() => {
    if (showcase) {
      setCounts({ [showcase.cityId]: showcase.restaurants.length });
      return;
    }
    let cancelled = false;
    setCountsError(false);
    listCityVenueCounts()
      .then((byCity) => {
        if (!cancelled) setCounts(byCity);
      })
      .catch(() => {
        if (!cancelled) {
          setCounts({});
          setCountsError(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [countsTick, showcase]);

  // Close the dropdown on outside tap or Escape.
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Reset the search whenever the menu closes so it reopens clean.
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const loading = counts === null;
  const isAvailable = (city: City) => (counts?.[city.id] ?? 0) > 0;

  function choose(city: City) {
    if (!isAvailable(city)) return;
    setSelected(city);
    setOpen(false);
  }

  return (
    <main className="app-ambient relative flex min-h-[100dvh] flex-col items-center justify-center bg-background px-5 py-16 text-foreground">
      {/* Fixed top-right cluster — mirrors the city feed: En/Ro pill on top with
          the account button stacked under it on phone, side-by-side on ≥sm. */}
      <div
        className="fixed right-3 z-50 flex flex-col-reverse items-end gap-2 sm:right-5 sm:flex-row sm:items-center"
        style={{ top: "max(0.75rem, env(safe-area-inset-top, 0px))" }}
      >
        {!showcase && (
          <button
            type="button"
            onClick={openAccount}
            aria-label={t("ACCOUNT_BUTTON")}
            aria-haspopup="dialog"
            className={cn(
              "grid h-10 w-10 place-items-center rounded-full border shadow-[0_0_0_1px_rgba(0,0,0,0.7)] light:shadow-none backdrop-blur transition sm:h-9 sm:w-9",
              accountInitial
                ? "border-white/20 light:border-black/15 bg-paper light:bg-ink font-display text-sm text-ink light:text-paper hover:bg-white light:hover:bg-zinc-800"
                : "border-white/15 light:border-black/15 bg-zinc-950/90 light:bg-white/90 text-zinc-300 light:text-zinc-700 hover:border-white/40 light:hover:border-black/35 hover:text-zinc-50 light:hover:text-zinc-900",
            )}
          >
            {accountInitial ?? <User className="h-4 w-4" aria-hidden />}
          </button>
        )}
        <LanguageToggle inline />
      </div>

      {/* No entrance animation: index.html pre-paints this exact header as a
          static shell, and a fade-in here would blank it out on React mount. */}
      <motion.div initial={false} className="w-full max-w-sm text-center">
        <h1 className="font-display text-4xl tracking-tight text-zinc-50 light:text-zinc-900 sm:text-5xl">
          {t("WELCOME_TITLE")}
        </h1>
        <p className="mt-3 text-pretty text-sm leading-relaxed text-zinc-300 light:text-zinc-700">{t("WELCOME_SUBTITLE")}</p>

        {/* City picker */}
        <div ref={menuRef} className="relative mt-10">
          <button
            type="button"
            onClick={() => {
              // First open on a fresh visit: request location NOW, tied to this
              // tap — the browser prompt appears over the opening menu and, on
              // grant, the nearest city pins to the top. "idle" means silent
              // mode found no prior grant; any other status means we already
              // asked (or were denied) and must not re-fire.
              if (!open && !showcase && geo.status === "idle") geo.request();
              setOpen((v) => !v);
            }}
            disabled={loading}
            aria-haspopup="listbox"
            aria-expanded={open}
            className={cn(
              "flex w-full items-center justify-between gap-3 rounded-xl border border-white/[0.12] light:border-black/10 bg-zinc-900/70 light:bg-white/70 px-4 py-3.5 text-left text-sm shadow-[0_1px_0_rgba(255,255,255,0.05)_inset] transition",
              "hover:border-white/30 light:hover:border-black/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300 light:focus-visible:ring-zinc-700 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950",
              loading && "cursor-wait opacity-70",
            )}
          >
            <span className="inline-flex items-center gap-2">
              <MapPin className="h-4 w-4 text-dim light:text-zinc-600" aria-hidden />
              <span className={selected ? "text-zinc-100 light:text-zinc-900" : "text-dim light:text-zinc-600"}>
                {selected ? selected.name : t("WELCOME_CITY_PLACEHOLDER")}
              </span>
            </span>
            <ChevronDown
              className={cn("h-4 w-4 text-dim light:text-zinc-600 transition-transform", open && "rotate-180")}
              aria-hidden
            />
          </button>

          <AnimatePresence>
            {open && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.16, ease: "easeOut" }}
                className="absolute z-20 mt-2 w-full overflow-hidden rounded-xl border border-white/15 light:border-black/15 bg-zinc-950/95 light:bg-white/95 shadow-[0_0_0_1px_#000,0_16px_32px_-16px_#000] backdrop-blur"
              >
                {/* Search — the list is every RO county capital, so filter to find yours fast. */}
                <div className="flex items-center gap-2 border-b border-white/10 light:border-black/10 px-3 py-2.5">
                  <Search className="h-4 w-4 shrink-0 text-dim light:text-zinc-600" aria-hidden />
                  <input
                    type="text"
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={onListKeyDown}
                    role="combobox"
                    aria-expanded={open}
                    aria-controls="city-listbox"
                    aria-activedescendant={
                      activeIdx >= 0 && filtered[activeIdx]
                        ? `city-opt-${filtered[activeIdx].id}`
                        : undefined
                    }
                    placeholder={t("WELCOME_CITY_SEARCH")}
                    aria-label={t("WELCOME_CITY_SEARCH")}
                    className="w-full bg-transparent text-base text-zinc-100 light:text-zinc-900 placeholder:text-zinc-500 focus:outline-none sm:text-sm"
                  />
                </div>

                <ul
                  id="city-listbox"
                  role="listbox"
                  aria-label={t("WELCOME_CITY_PLACEHOLDER")}
                  className="max-h-64 overflow-y-auto p-1"
                >
                  {filtered.map((city, idx) => {
                    const isSelected = selected?.id === city.id;
                    const count = counts?.[city.id] ?? 0;
                    const enabled = count > 0;
                    return (
                      <li
                        key={city.id}
                        id={`city-opt-${city.id}`}
                        role="option"
                        aria-selected={isSelected}
                        aria-disabled={!enabled}
                      >
                        <button
                          type="button"
                          disabled={!enabled}
                          onClick={() => choose(city)}
                          tabIndex={-1}
                          className={cn(
                            "flex w-full items-center justify-between gap-2 rounded-lg px-3 py-3 text-left text-sm transition",
                            enabled
                              ? "text-zinc-100 light:text-zinc-900 hover:bg-zinc-800/70 light:hover:bg-zinc-100"
                              : "cursor-not-allowed text-zinc-600",
                            idx === activeIdx && (enabled ? "bg-zinc-800/70 light:bg-zinc-200/70" : "bg-zinc-900/60 light:bg-white/70"),
                          )}
                        >
                          <span className="inline-flex items-baseline gap-2">
                            {city.name}
                            {enabled && (
                              <>
                                <span className="font-mono text-xs tabular-nums text-dim light:text-zinc-600" aria-hidden>
                                  {count}
                                </span>
                                <span className="sr-only">
                                  {count} {t(count === 1 ? "WELCOME_CITY_VENUE" : "WELCOME_CITY_VENUES")}
                                </span>
                              </>
                            )}
                          </span>
                          <span className="flex shrink-0 items-center gap-1.5">
                            {city.id === nearest?.id && (
                              <span className="inline-flex items-center gap-1 rounded-full border border-pulse/40 bg-pulse/10 px-2 py-0.5 font-mono text-3xs font-medium uppercase tracking-[0.14em] text-pulse">
                                <Navigation className="h-2.5 w-2.5" aria-hidden />
                                {t("WELCOME_CITY_NEAREST")}
                              </span>
                            )}
                            {enabled ? (
                              isSelected ? <Check className="h-4 w-4 text-zinc-300 light:text-zinc-700" aria-hidden /> : null
                            ) : (
                              <span className="rounded-full border border-white/10 light:border-black/10 px-2 py-0.5 font-mono text-3xs font-medium uppercase tracking-[0.14em] text-dim light:text-zinc-600">
                                {t("WELCOME_CITY_SOON")}
                              </span>
                            )}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                  {filtered.length === 0 && (
                    <li className="px-3 py-4 text-center text-sm text-dim light:text-zinc-600">{t("WELCOME_CITY_NONE")}</li>
                  )}
                  {hiddenSoon && (
                    <li className="px-3 pb-2 pt-3 text-center text-xs text-dim light:text-zinc-600" aria-hidden>
                      {t("WELCOME_MORE_CITIES_SOON")}
                    </li>
                  )}
                </ul>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {countsError && (
          <p className="mt-3 text-xs text-dim light:text-zinc-600">
            {t("WELCOME_CITIES_ERROR")}{" "}
            <button
              type="button"
              onClick={() => {
                setCounts(null);
                setCountsTick((n) => n + 1);
              }}
              className="font-medium text-zinc-300 light:text-zinc-700 underline underline-offset-2 transition hover:text-zinc-50 light:hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300 light:focus-visible:ring-zinc-700"
            >
              {t("RETRY")}
            </button>
          </p>
        )}

        {/* Continue — enabled once a (live) city is selected */}
        <button
          type="button"
          disabled={!selected}
          onClick={() =>
            selected &&
            navigate(showcase ? `${showcase.basePath}/${citySlug(selected.id)}` : `/${citySlug(selected.id)}`)
          }
          className={cn(
            "mt-5 inline-flex w-full items-center justify-center rounded-xl px-4 py-3.5 text-sm font-semibold transition",
            selected
              ? "bg-paper light:bg-ink text-ink light:text-paper shadow-[0_4px_18px_-6px_rgba(245,241,232,0.35)] hover:bg-white light:hover:bg-zinc-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300 light:focus-visible:ring-zinc-700 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
              : "cursor-not-allowed border border-white/10 light:border-black/10 bg-zinc-900 light:bg-white text-dim light:text-zinc-600",
          )}
        >
          {t("WELCOME_CONTINUE")}
        </button>
      </motion.div>

      {/* Account & settings sidebar (lazy) — its Terms/Privacy links return via
          history-back, landing back here on the gate. */}
      {accountMounted && (
        <Suspense fallback={null}>
          <AccountSidebar open={accountOpen} onClose={() => setAccountOpen(false)} geo={geo} />
        </Suspense>
      )}
    </main>
  );
}
